import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { COMPANY } from 'services/consts';

import { setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES } from 'utils/constants';

import * as S from '../ImportOrderOnboardingModal.styled';

const defaultColSpan = {
	md: 6
};

const VerifyCRSection = ({ closeModal, nextStep }) => {
	const isAuthorized = useAccessControl();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const generalSettings = useSelector((state) => state?.user?.generalSettings);
	const [isVerifyFailed, setIsVerifyFailed] = useState(false);

	const { CRNumber, unifiedNumber, invoiceType, BankIban, CRDocumentURL, civilID } = generalSettings;
	const schema = useMemo(() => {
		return yup.object().shape({
			CRNumber: yup
				.string()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => {
					if (!isVerifyFailed && isAuthorized('/company-settings/CommonInvoiceType')) {
						return schema.checkCRNumber().required();
					}
					return schema;
				}),
			CRDocumentURL: yup
				.mixed()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => {
					if (isVerifyFailed) {
						return schema.required();
					}
					return schema;
				}),
			civilID: yup
				.mixed()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => {
					if (isVerifyFailed) {
						return schema.required();
					}
					return schema;
				}),
			unifiedNumber: yup
				.string()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => {
					if (!isVerifyFailed) {
						return schema
							.matches(
								/^7\d{9}$/,
								t('validation.numberFieldLengthStartsWith', {
									maxLength: 10,
									startsWith: 7
								})
							)
							.required();
					}
					return schema;
				}),

			BankIban: yup
				.string()
				.nullable()
				.when(['$isVerifyFailed'], ([isVerifyFailed], schema) => (isVerifyFailed ? schema : schema.required()))
				.test('iban-space-control', t('error.notValidIBANFormat'), (value) => {
					if (!value) return true;
					return !/\s/.test(value);
				})
				.test('iban-format-control', t('error.notValidIBANFormatKSA'), (value) => {
					if (!value) return true;
					const trimmedValue = value.replace(/\s+/g, '');
					return trimmedValue.startsWith('SA') && trimmedValue.length === 24;
				})
		});
	}, [isAuthorized]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			CRNumber,
			unifiedNumber,
			invoiceType: ACCOUNT_TYPES.COMPANY,
			BankIban,
			CRDocumentURL,
			civilID
		},
		context: { isVerifyFailed }
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const [autoAttemptCheckLoaded, setAutoAttemptCheckLoaded] = useState(false);

	const onSubmit = async (data) => {
		try {
			const req = {
				...data,
				invoiceType: ACCOUNT_TYPES.COMPANY,
				CRNumber: data.CRNumber,
				unifiedNumber: data.unifiedNumber,
				CRDocumentURL: data.CRDocumentURL,
				BankIban: data.BankIban,
				civilID: data.civilID?.path || data?.civilID || null
			};
			let response;
			if (isVerifyFailed) {
				response = await axiosPrivate.post(COMPANY.UPLOAD_DOCUMENTS, {
					CRDocument: data.CRDocumentURL?.path || data?.CRDocumentURL,
					civilID: data.civilID?.path || data?.civilID
				});
			} else {
				response = await axiosPrivate.post(COMPANY.VERIFY_CR_DOCUMENT, req);
			}
			if (response.data.docStatus === 'VERIFICATION_FAILED') {
				setIsVerifyFailed(true);
			} else if (response.data.docStatus === 'UNVERIFIED') {
				if (response.data.reason === 'ATTEMPT_COUNT_EXCEEDED') setIsVerifyFailed(true);
				showError({
					header:
						response.data.reason === 'DUPLICATE_RECORD'
							? 'cr.duplicateRecord.errorTitle'
							: response.data.reason === 'ATTEMPT_COUNT_EXCEEDED'
								? 'cr.attemptLimitExceed.title'
								: 'crVerification.unverified.title',
					message:
						response.data.reason === 'DUPLICATE_RECORD'
							? 'cr.duplicateRecord.errorDescription'
							: response.data.reason === 'ATTEMPT_COUNT_EXCEEDED'
								? 'cr.attemptLimitExceed.description'
								: 'crVerification.unverified.description'
				});
			} else if (response.status === 200) {
				if (!isVerifyFailed) {
					dispatch(
						setUserGeneralSettings({
							...req,
							BankIban: data.BankIban,
							CRDocStatus: 'VERIFIED'
						})
					);
					showSuccess();
				} else {
					await axiosPrivate.post(COMPANY.SET_INVOICE_TYPE, { invoiceType: data.invoiceType });
					dispatch(
						setUserGeneralSettings({
							invoiceType: ACCOUNT_TYPES.COMPANY,
							CRDocumentURL: data.CRDocumentURL?.path || data?.CRDocumentURL,
							civilID: data.civilID?.path || data?.civilID
						})
					);
					showSuccess({
						header: 'verifyDocumentUploaded',
						message: 'verifyDocumentUploadedDescription'
					});
				}
				nextStep();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const checkAutoVerifyAttempt = async () => {
		try {
			const { data, status } = await axiosPrivate.post(COMPANY.CHECK_AUTO_VERIFY_ATTEMPT, {});
			const failReason = data?.reason;
			if (status === 200 && failReason && failReason === 'ATTEMPT_COUNT_EXCEEDED') {
				setIsVerifyFailed(true);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setAutoAttemptCheckLoaded(true);
		}
	};

	useEffect(() => {
		checkAutoVerifyAttempt();
	}, []);

	return (
		<>
			{autoAttemptCheckLoaded && (
				<FormProvider {...formMethods}>
					<ModalContent>
						{invoiceType !== ACCOUNT_TYPES.COMPANY && !isVerifyFailed && (
							<S.OnboardingAlert variant="warning" showIcon content="import.onboarding.warning" />
						)}
						{isVerifyFailed && <S.OnboardingAlert variant="warning" content="crVerifyFailed" showIcon />}
						<Form formMethods={formMethods} onSubmit={handleSubmit(onSubmit)}>
							{isVerifyFailed ? (
								<>
									<FormItem
										colSpan={defaultColSpan}
										label="CRDocument"
										isLoading={isLoading}
										required>
										<AdvanceFileInput
											name="CRDocumentURL"
											btnStyle={{ width: '100%' }}
											acceptedFileType={'*'}
											setValue={(value) => {
												setValue('CRDocumentURL', value?.path ? value : null, {
													shouldValidate: true,
													shouldDirty: true
												});
											}}
											value={{ name: 'CRDocumentURL', path: watch('CRDocumentURL') ?? null }}
											maxFileSize={1000000}
										/>
									</FormItem>
									<FormItem col={6} label="civilIDOrIqama" isLoading={isLoading} required>
										<AdvanceFileInput
											name="civilID"
											style={{ width: '100%' }}
											btnStyle={{ width: '100%' }}
											acceptedFileType={'*'}
											setValue={(value) => {
												setValue('civilID', value?.path ? value : null, {
													shouldValidate: true,
													shouldDirty: true
												});
											}}
											value={{
												name: 'civilID',
												path: watch('civilID') ?? null
											}}
											maxFileSize={1000000}
										/>
									</FormItem>
								</>
							) : (
								<>
									<FormItem colSpan={defaultColSpan} label="CRNumber" isLoading={isLoading} required>
										<StringInput name="CRNumber" allowOnlyNumbers maxlength={10} />
									</FormItem>
									<FormItem
										colSpan={defaultColSpan}
										label="unifiedNumber"
										isLoading={isLoading}
										required>
										<StringInput name="unifiedNumber" maxlength={10} allowOnlyNumbers />
									</FormItem>
								</>
							)}

							{!isVerifyFailed && (
								<FormItem colSpan={defaultColSpan} label="ibanNumber" isLoading={isLoading} required>
									<StringInput name="BankIban" />
									<Typography className="mt-2" noWrap color="var(--content-2)">
										ibanNumber.CRtooltip
									</Typography>
								</FormItem>
							)}
						</Form>
					</ModalContent>

					<ModalActions>
						<Button variant="outline" label="cancel" onClick={closeModal} />
						<Button
							label="continue"
							iconPos="right"
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							variant="blue"
							loading={isLoading}
						/>
					</ModalActions>
				</FormProvider>
			)}
		</>
	);
};

export default VerifyCRSection;
